import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { HitApi } from '../../Api/ApiHit'
import { searchUser, searchBill, deleteBill } from '../../constant/Constant'
import { getSelectedUnit, getElevateUser } from '../../storage/Storage'
import { ChevronLeft, ChevronRight, User, Calendar, Plus, Lock, X, Trash2, Clock } from 'lucide-react'
import toast from 'react-hot-toast'

const SLOT_GAP_MINUTES = 40

// 15-min interval slots from 8 AM to 9 PM
const generateTimeSlots = () => {
    const slots = []
    for (let hour = 8; hour <= 20; hour++) {
        for (let min = 0; min < 60; min += 15) {
            if (hour === 20 && min > 0) break
            const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
            const ampm = hour >= 12 ? 'PM' : 'AM'
            const label = `${h} ${ampm}`
            slots.push({ label, hour, minute: min, totalMinutes: hour * 60 + min })
        }
    }
    return slots
}

const TIME_SLOTS = generateTimeSlots()

const CARD_COLORS = [
    { bg: '#deeeff', border: '#b8d4f0' },  // blue
    { bg: '#ffdef0', border: '#f0b8d0' },  // pink
    { bg: '#efefef', border: '#d0d0d0' },  // gray
    { bg: '#e5e0ff', border: '#c8c0e8' },  // purple
]

const getCardColor = (index) => CARD_COLORS[index % CARD_COLORS.length]

// Convert totalMinutes to display label "10 AM" or "2 PM"
const minutesToLabel = (totalMinutes) => {
    const hour24 = Math.floor(totalMinutes / 60)
    const min = totalMinutes % 60
    const h = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    if (min === 0) return `${h} ${ampm}`
    return `${h}:${String(min).padStart(2, '0')} ${ampm}`
}

// Convert totalMinutes to 24h "HH:MM" for query param
const minutesToTimeParam = (totalMinutes) => {
    const hour = Math.floor(totalMinutes / 60)
    const min = totalMinutes % 60
    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

// Parse billTime (4:30:00 pm or 16:30) into totalMinutes from midnight
const parseBillTime = (billTime) => {
    if (!billTime) return null
    let timeStr = billTime.toLowerCase().trim()
    let hours = 0
    let minutes = 0

    if (timeStr.includes('am') || timeStr.includes('pm')) {
        const isPM = timeStr.includes('pm')
        timeStr = timeStr.replace(/am|pm/gi, '').trim()
        const timeParts = timeStr.split(':')
        hours = parseInt(timeParts[0])
        minutes = parseInt(timeParts[1] || '0')
        if (isPM && hours !== 12) hours += 12
        else if (!isPM && hours === 12) hours = 0
    } else {
        const timeParts = timeStr.split(':')
        hours = parseInt(timeParts[0])
        minutes = parseInt(timeParts[1] || '0')
    }

    return { hours, minutes, totalMinutes: hours * 60 + minutes }
}

const SLOT_HEIGHT = 20 // px per 15-min slot
const HOUR_HEIGHT = SLOT_HEIGHT * 4 // 80px per hour

function AppoinmentMaster() {
    const navigate = useNavigate()
    const loggedInUser = getElevateUser()
    const isSuperAdmin = loggedInUser?.roleData?.roleType === 'SuperAdmin'
    const [users, setUsers] = useState([])
    const [appointments, setAppointments] = useState([])
    const [selectedDate, setSelectedDate] = useState(new Date())
    const [selectedUser, setSelectedUser] = useState(null)
    const [isLoadingUsers, setIsLoadingUsers] = useState(false)
    const [isLoadingAppointments, setIsLoadingAppointments] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedAppointment, setSelectedAppointment] = useState(null)
    const [staffDetailsMap, setStaffDetailsMap] = useState({})
    const [showCalendar, setShowCalendar] = useState(false)
    const [calendarMonth, setCalendarMonth] = useState(new Date())
    const scrollRef = useRef(null)
    const calendarRef = useRef(null)

    // Drag-to-select state
    const [isDragging, setIsDragging] = useState(false)
    const [dragUserId, setDragUserId] = useState(null)
    const [dragStartSlot, setDragStartSlot] = useState(null)
    const [dragEndSlot, setDragEndSlot] = useState(null)
    const [showSlotPopup, setShowSlotPopup] = useState(false)
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 })
    const [popupStartMin, setPopupStartMin] = useState(null) // editable start time in popup
    const [popupEndMin, setPopupEndMin] = useState(null)     // editable end time in popup
    const [showStartDropdown, setShowStartDropdown] = useState(false)
    const [showEndDropdown, setShowEndDropdown] = useState(false)
    const popupRef = useRef(null)
    const startDropdownRef = useRef(null)
    const endDropdownRef = useRef(null)

    // Close calendar on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (calendarRef.current && !calendarRef.current.contains(e.target)) {
                setShowCalendar(false)
            }
        }
        if (showCalendar) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showCalendar])

    // Close slot popup on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                setShowSlotPopup(false)
                setDragStartSlot(null)
                setDragEndSlot(null)
                setDragUserId(null)
            }
        }
        if (showSlotPopup) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showSlotPopup])

    // Global mouseup to end drag
    useEffect(() => {
        const handleMouseUp = (e) => {
            if (isDragging && dragStartSlot !== null && dragEndSlot !== null) {
                setIsDragging(false)
                const s = Math.min(dragStartSlot, dragEndSlot)
                const en = Math.max(dragStartSlot, dragEndSlot) + 15
                setPopupStartMin(s)
                setPopupEndMin(en)
                setPopupPosition({ x: e.clientX, y: e.clientY })
                setShowSlotPopup(true)
                setShowStartDropdown(false)
                setShowEndDropdown(false)
            } else if (isDragging) {
                setIsDragging(false)
            }
        }
        if (isDragging) {
            document.addEventListener('mouseup', handleMouseUp)
        }
        return () => document.removeEventListener('mouseup', handleMouseUp)
    }, [isDragging, dragStartSlot, dragEndSlot])

    // Sync calendarMonth when selectedDate changes
    useEffect(() => {
        setCalendarMonth(new Date(selectedDate))
    }, [selectedDate])

    // Drag helpers
    const getDragRange = () => {
        if (dragStartSlot === null || dragEndSlot === null) return { start: null, end: null }
        const startMin = Math.min(dragStartSlot, dragEndSlot)
        const endMin = Math.max(dragStartSlot, dragEndSlot) + 15
        return { start: startMin, end: endMin }
    }

    const isSlotInDragRange = (slotTotalMinutes, userId) => {
        if (!isDragging && !showSlotPopup) return false
        if (userId !== dragUserId) return false
        const { start, end } = getDragRange()
        if (start === null) return false
        return slotTotalMinutes >= start && slotTotalMinutes < end
    }

    const handleDragStart = (slot, userId) => {
        setShowSlotPopup(false)
        setIsDragging(true)
        setDragUserId(userId)
        setDragStartSlot(slot.totalMinutes)
        setDragEndSlot(slot.totalMinutes)
    }

    const handleDragMove = (slot, userId) => {
        if (!isDragging || userId !== dragUserId) return
        // Prevent dragging past a blocked/booked slot
        const dragStart = dragStartSlot
        const proposedEnd = slot.totalMinutes
        if (dragStart !== null) {
            const rangeStart = Math.min(dragStart, proposedEnd)
            const rangeEnd = Math.max(dragStart, proposedEnd) + 15
            const nextBlock = getNextBlockedStart(rangeStart, userId)
            if (rangeEnd > nextBlock) return // Don't extend past a booked slot
        }
        setDragEndSlot(slot.totalMinutes)
    }

    const handleSlotPopupConfirm = () => {
        const start = popupStartMin
        const end = popupEndMin
        if (start === null || end === null || end <= start) {
            toast.error('Please select a valid time range')
            return
        }
        // Block overlapping bookings
        if (doesRangeOverlap(start, end, dragUserId)) {
            toast.error('This time slot overlaps with an existing appointment')
            return
        }
        // Block past slot booking
        if (isSlotPast(start)) {
            toast.error('Cannot book appointments in the past')
            setShowSlotPopup(false)
            setDragStartSlot(null)
            setDragEndSlot(null)
            setDragUserId(null)
            return
        }
        const dateParam = formatDateParam(selectedDate)
        const startTime = minutesToTimeParam(start)
        const endTime = minutesToTimeParam(end)
        setShowSlotPopup(false)
        setDragStartSlot(null)
        setDragEndSlot(null)
        navigate(`/appoinments/add?date=${dateParam}&time=${startTime}&slotEnd=${endTime}&staffId=${dragUserId}`)
        setDragUserId(null)
    }

    // Generate time options for dropdowns (15 min intervals from 8 AM to 9 PM)
    const generateTimeOptions = () => {
        const options = []
        for (let h = 8; h <= 20; h++) {
            for (let m = 0; m < 60; m += 15) {
                if (h === 20 && m > 0) break
                options.push(h * 60 + m)
            }
        }
        return options
    }
    const timeOptions = generateTimeOptions()

    // Format duration label
    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes} mins`
        const hrs = Math.floor(minutes / 60)
        const mins = minutes % 60
        if (mins === 0) return hrs === 1 ? '1 hr' : `${hrs} hrs`
        return `${hrs}.${mins === 30 ? '5' : mins === 15 ? '25' : '75'} hrs`
    }

    // Modal helpers
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    }

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0'
        return `₹${Number(amount).toFixed(2)}`
    }

    const renderBillStatus = (status) => {
        const statusText = status || 'pending'
        let bgColor = 'bg-gray-100'
        let textColor = 'text-gray-800'
        if (statusText === 'completed') { bgColor = 'bg-green-100'; textColor = 'text-green-800' }
        else if (statusText === 'cancelled') { bgColor = 'bg-red-100'; textColor = 'text-red-800' }
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {statusText.charAt(0).toUpperCase() + statusText.slice(1)}
            </span>
        )
    }

    const renderPaymentStatus = (payment) => {
        const status = payment?.paymentStatus || 'Pending'
        let bgColor = 'bg-yellow-100'
        let textColor = 'text-yellow-800'
        if (status === 'Paid') { bgColor = 'bg-green-100'; textColor = 'text-green-800' }
        else if (status === 'Partial') { bgColor = 'bg-orange-100'; textColor = 'text-orange-800' }
        else if (status === 'Unpaid') { bgColor = 'bg-red-100'; textColor = 'text-red-800' }
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {status}
            </span>
        )
    }

    const handleViewAppointment = (apt) => {
        setSelectedAppointment(apt)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedAppointment(null)
        setStaffDetailsMap({})
    }

    const handleDeleteAppointment = async () => {
        if (!selectedAppointment?._id) return
        if (!window.confirm('Are you sure you want to delete this appointment?')) return
        try {
            const json = { _id: selectedAppointment._id }
            const res = await HitApi(json, deleteBill)
            if (res?.statusCode === 200) {
                toast.success('Appointment deleted successfully')
                handleCloseModal()
                fetchAppointments()
            } else {
                toast.error(res?.message || 'Failed to delete appointment')
            }
        } catch (error) {
            console.error('Error deleting appointment:', error)
            toast.error('Failed to delete appointment')
        }
    }

    // Fetch staff details for modal
    useEffect(() => {
        if (!selectedAppointment) return
        const fetchStaffDetails = async () => {
            const staffIds = new Set()
            if (selectedAppointment.services && Array.isArray(selectedAppointment.services)) {
                selectedAppointment.services.forEach(service => {
                    if (service.staff) {
                        const staffId = typeof service.staff === 'object' ? service.staff._id : service.staff
                        if (staffId) staffIds.add(staffId)
                    }
                })
            }
            const staffDetailsTemp = {}
            for (const staffId of staffIds) {
                try {
                    const json = { page: 1, limit: 1, search: { _id: staffId } }
                    const res = await HitApi(json, searchUser)
                    if (res?.statusCode === 200 && res?.data?.docs?.[0]) {
                        staffDetailsTemp[staffId] = res.data.docs[0]
                    }
                } catch (error) {
                    console.error(`Error fetching staff details for ID ${staffId}:`, error)
                }
            }
            setStaffDetailsMap(staffDetailsTemp)
        }
        fetchStaffDetails()
    }, [selectedAppointment])

    // Date helpers
    const formatDisplayDate = (date) => {
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    }

    const formatDateParam = (date) => {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }

    const goToToday = () => setSelectedDate(new Date())
    const goToPrevDay = () => {
        const prev = new Date(selectedDate)
        prev.setDate(prev.getDate() - 1)
        setSelectedDate(prev)
    }
    const goToNextDay = () => {
        const next = new Date(selectedDate)
        next.setDate(next.getDate() + 1)
        setSelectedDate(next)
    }

    // Fetch all users/staff
    const fetchUsers = useCallback(async () => {
        setIsLoadingUsers(true)
        try {
            if (isSuperAdmin) {
                const json = {
                    page: 1,
                    limit: 50,
                    search: {
                        unitIds: getSelectedUnit()?._id,
                        userType: { $in: ['Staff', 'Admin'] }
                    }
                }
                const res = await HitApi(json, searchUser)
                if (res?.statusCode === 200) {
                    setUsers(Array.isArray(res?.data?.docs) ? res.data.docs : [])
                }
            } else {
                // Non-SuperAdmin: only show their own profile
                const json = {
                    page: 1,
                    limit: 1,
                    search: { _id: loggedInUser?._id }
                }
                const res = await HitApi(json, searchUser)
                if (res?.statusCode === 200 && res?.data?.docs?.[0]) {
                    setUsers([res.data.docs[0]])
                } else {
                    setUsers([loggedInUser])
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error)
            toast.error('Failed to load staff members')
        } finally {
            setIsLoadingUsers(false)
        }
    }, [])

    // Fetch appointments for selected date
    const fetchAppointments = useCallback(async () => {
        setIsLoadingAppointments(true)
        try {
            const startOfDay = new Date(selectedDate)
            startOfDay.setHours(0, 0, 0, 0)
            const endOfDay = new Date(selectedDate)
            endOfDay.setHours(23, 59, 59, 999)

            const json = {
                page: 1,
                limit: 500,
                search: {
                    billType: "APPOINTMENT",
                    unitId: getSelectedUnit()?._id,
                    date: {
                        $gte: startOfDay.getTime(),
                        $lte: endOfDay.getTime()
                    }
                }
            }

            const res = await HitApi(json, searchBill)
            if (res?.statusCode === 200) {
                setAppointments(Array.isArray(res?.data?.docs) ? res.data.docs : [])
            }
        } catch (error) {
            console.error('Error fetching appointments:', error)
        } finally {
            setIsLoadingAppointments(false)
        }
    }, [selectedDate])

    useEffect(() => { fetchUsers() }, [fetchUsers])
    useEffect(() => { fetchAppointments() }, [fetchAppointments])

    // Scroll to current time on load
    useEffect(() => {
        if (scrollRef.current) {
            const now = new Date()
            const currentMinutes = now.getHours() * 60 + now.getMinutes()
            const scrollTo = ((currentMinutes - 480) / 60) * HOUR_HEIGHT
            scrollRef.current.scrollTop = Math.max(0, scrollTo - 120)
        }
    }, [users, selectedUser])

    // Get appointments for a specific user
    const getAppointmentsForUser = (userId) => {
        return appointments.filter(apt => {
            if (apt.services && Array.isArray(apt.services)) {
                return apt.services.some(s => {
                    const staffId = typeof s.staff === 'object' ? (s.staff?._id || s.staff?.id) : s.staff
                    return staffId === userId
                })
            }
            return false
        })
    }

    // Get blocked time ranges for a user
    const getBlockedRanges = (userId) => {
        const userApts = getAppointmentsForUser(userId)
        const ranges = []
        userApts.forEach(apt => {
            const parsed = parseBillTime(apt.billTime)
            if (parsed) {
                let endMinutes = parsed.totalMinutes + SLOT_GAP_MINUTES
                if (apt.slotStart && apt.slotEnd) {
                    const endParsed = parseBillTime(apt.slotEnd)
                    if (endParsed) endMinutes = endParsed.totalMinutes
                }
                ranges.push({
                    start: parsed.totalMinutes,
                    end: endMinutes
                })
            }
        })
        return ranges
    }

    const isSlotBlocked = (slotTotalMinutes, blockedRanges) => {
        return blockedRanges.some(range => slotTotalMinutes >= range.start && slotTotalMinutes < range.end)
    }

    // Check if a proposed range overlaps with any existing bookings for a user
    const doesRangeOverlap = (startMin, endMin, userId) => {
        const blockedRanges = getBlockedRanges(userId)
        return blockedRanges.some(range => startMin < range.end && endMin > range.start)
    }

    // Find the next blocked slot boundary after a given start time (for limiting drag)
    const getNextBlockedStart = (startMin, userId) => {
        const blockedRanges = getBlockedRanges(userId)
        let nextBlock = Infinity
        blockedRanges.forEach(range => {
            if (range.start > startMin && range.start < nextBlock) {
                nextBlock = range.start
            }
        })
        return nextBlock
    }

    // Current time
    const now = new Date()
    const currentTimeMinutes = now.getHours() * 60 + now.getMinutes()
    const currentTimeTop = ((currentTimeMinutes - 480) / 60) * HOUR_HEIGHT
    const isToday = selectedDate.toDateString() === new Date().toDateString()
    const isPastDate = selectedDate < new Date(new Date().toDateString())

    // Check if a slot is in the past (past date or past time on today)
    const isSlotPast = (slotTotalMinutes) => {
        if (isPastDate) return true
        if (isToday && slotTotalMinutes < currentTimeMinutes) return true
        return false
    }

    // Display users
    const displayUsers = selectedUser ? [selectedUser] : users

    // Generate hour labels (only full hours)
    const hourLabels = []
    for (let h = 8; h <= 20; h++) {
        const display = h > 12 ? `${h - 12} PM` : h === 12 ? '12 PM' : `${h} AM`
        hourLabels.push({ hour: h, label: display, topMinutes: h * 60 })
    }

    return (
        <div className='flex flex-col h-[calc(100vh-80px)] bg-white'>
            {/* Top Navigation Bar */}
            <div className='flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200'>
                <div className='flex items-center gap-5'>
                    <button onClick={goToToday} className='px-5 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-all'>
                        Today
                    </button>
                    <div className='flex items-center gap-0.5'>
                        <button onClick={goToPrevDay} className='p-2 rounded-full hover:bg-gray-100 transition-colors'>
                            <ChevronLeft className='w-5 h-5 text-gray-500' />
                        </button>
                        <button onClick={goToNextDay} className='p-2 rounded-full hover:bg-gray-100 transition-colors'>
                            <ChevronRight className='w-5 h-5 text-gray-500' />
                        </button>
                    </div>
                    <div className='relative' ref={calendarRef}>
                        <h2
                            className='text-[22px] font-normal text-gray-700 tracking-tight cursor-pointer hover:text-blue-600 transition-colors select-none'
                            onClick={() => setShowCalendar(!showCalendar)}
                        >
                            {formatDisplayDate(selectedDate)}
                        </h2>

                        {/* Mini Calendar Dropdown */}
                        {showCalendar && (() => {
                            const year = calendarMonth.getFullYear()
                            const month = calendarMonth.getMonth()
                            const firstDay = new Date(year, month, 1).getDay()
                            const daysInMonth = new Date(year, month + 1, 0).getDate()
                            const daysInPrevMonth = new Date(year, month, 0).getDate()
                            const today = new Date()
                            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                            const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

                            const cells = []
                            for (let i = firstDay - 1; i >= 0; i--) {
                                cells.push({ day: daysInPrevMonth - i, current: false })
                            }
                            for (let d = 1; d <= daysInMonth; d++) {
                                cells.push({ day: d, current: true })
                            }
                            const remaining = 42 - cells.length
                            for (let d = 1; d <= remaining; d++) {
                                cells.push({ day: d, current: false })
                            }

                            return (
                                <div className='absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 z-[60] w-[280px]'>
                                    <div className='flex items-center justify-between mb-2'>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setCalendarMonth(new Date(year, month - 1, 1)) }}
                                            className='p-1 rounded-full hover:bg-gray-100 transition-colors'
                                        >
                                            <ChevronLeft className='w-4 h-4 text-gray-500' />
                                        </button>
                                        <span className='text-sm font-semibold text-gray-800'>{monthNames[month]} {year}</span>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setCalendarMonth(new Date(year, month + 1, 1)) }}
                                            className='p-1 rounded-full hover:bg-gray-100 transition-colors'
                                        >
                                            <ChevronRight className='w-4 h-4 text-gray-500' />
                                        </button>
                                    </div>
                                    <div className='grid grid-cols-7 mb-1'>
                                        {dayNames.map(d => (
                                            <div key={d} className='text-center text-[10px] font-semibold text-gray-400 py-1'>{d}</div>
                                        ))}
                                    </div>
                                    <div className='grid grid-cols-7'>
                                        {cells.map((cell, i) => {
                                            const isSelected = cell.current && cell.day === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear()
                                            const isToday2 = cell.current && cell.day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
                                            return (
                                                <button
                                                    key={i}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        if (cell.current) {
                                                            setSelectedDate(new Date(year, month, cell.day))
                                                            setShowCalendar(false)
                                                        }
                                                    }}
                                                    className={`w-9 h-9 flex items-center justify-center text-xs rounded-full transition-all
                                                        ${!cell.current ? 'text-gray-300' : 'text-gray-700 hover:bg-gray-100'}
                                                        ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700 font-semibold' : ''}
                                                        ${isToday2 && !isSelected ? 'text-blue-600 font-bold ring-1 ring-blue-300' : ''}
                                                    `}
                                                >
                                                    {cell.day}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>
                </div>
                <div className='flex items-center gap-3'>
                    <span className='text-xs text-gray-400'>{appointments.length} appointments</span>
                </div>
            </div>

            {/* Staff Header + Calendar Grid */}
            <div className='flex-1 overflow-hidden flex flex-col'>

                {/* Staff Column Headers */}
                <div className='flex border-b border-gray-200 bg-white flex-shrink-0'>
                    {/* Time column spacer */}
                    <div className='w-[80px] min-w-[80px] flex-shrink-0 border-r border-gray-200 flex items-center justify-center py-4'>
                        <span className='text-[11px] text-gray-400 font-medium'>GMT +5:30</span>
                    </div>

                    {/* Staff names */}
                    <div className='flex flex-1 overflow-x-auto' id='staff-header-scroll'>
                        {isLoadingUsers ? (
                            <div className='flex items-center justify-center w-full py-4'>
                                <div className='animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full'></div>
                                <span className='ml-2 text-gray-400 text-sm'>Loading...</span>
                            </div>
                        ) : users.length > 0 ? (
                            <>
                                {/* All Staff button - only for SuperAdmin */}
                                {isSuperAdmin && selectedUser && (
                                    <div
                                        className='flex items-center gap-3 px-5 py-4 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors bg-blue-50'
                                        style={{ minWidth: '140px', flex: '0 0 140px' }}
                                        onClick={() => setSelectedUser(null)}
                                    >
                                        <div className='w-9 h-9 rounded-full flex items-center justify-center bg-blue-600 flex-shrink-0'>
                                            <Calendar className='w-4 h-4 text-white' />
                                        </div>
                                        <span className='text-sm font-medium text-blue-700'>All Staff</span>
                                    </div>
                                )}
                                {displayUsers.map((user, idx) => {
                                const aptCount = getAppointmentsForUser(user._id).length
                                return (
                                    <div
                                        key={user._id}
                                        className='flex items-center gap-3 px-5 py-4 border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors relative'
                                        style={{ minWidth: '220px', flex: '1 0 220px' }}
                                        onClick={() => setSelectedUser(selectedUser?._id === user._id ? null : user)}
                                    >
                                        <div className='relative flex-shrink-0'>
                                            <div className='w-9 h-9 rounded-full overflow-hidden bg-gray-200'>
                                                {user.profileImage || user.img ? (
                                                    <img
                                                        src={user.profileImage || user.img}
                                                        alt={user.name}
                                                        className='w-full h-full object-cover'
                                                        onError={(e) => {
                                                            e.target.style.display = 'none'
                                                            e.target.parentElement.querySelector('.fallback')?.classList.remove('hidden')
                                                        }}
                                                    />
                                                ) : null}
                                                <div className={`fallback w-full h-full flex items-center justify-center bg-gray-300 text-gray-600 text-sm font-semibold ${user.profileImage || user.img ? 'hidden' : ''}`}>
                                                    {(user.name || 'U').charAt(0).toUpperCase()}
                                                </div>
                                            </div>
                                            {aptCount > 0 && (
                                                <span className='absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-blue-600 text-white rounded-full px-1 shadow-sm'>
                                                    {aptCount}
                                                </span>
                                            )}
                                        </div>
                                        <span className='text-sm font-medium text-gray-800 truncate'>
                                            {user.name || 'Unknown'}
                                        </span>
                                    </div>
                                )
                            })}
                            </>
                        ) : (
                            <div className='flex items-center justify-center w-full py-4'>
                                <span className='text-gray-400 text-sm'>No staff members found</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Calendar Grid Body */}
                <div ref={scrollRef} className='flex-1 overflow-auto relative bg-white'>
                    <div className='flex' style={{ minWidth: displayUsers.length > 0 ? `${80 + displayUsers.length * 220}px` : '100%' }}>

                        {/* Time Column */}
                        <div className='w-[80px] min-w-[80px] flex-shrink-0 border-r border-gray-200 sticky left-0 z-10 bg-white'>
                            {hourLabels.map((h) => (
                                <div
                                    key={h.hour}
                                    className='relative'
                                    style={{ height: `${HOUR_HEIGHT}px` }}
                                >
                                    <span className='absolute -top-[8px] right-4 text-[11px] text-gray-400 font-normal'>
                                        {h.label}
                                    </span>
                                </div>
                            ))}
                            {/* Current time indicator - dot */}
                            {isToday && currentTimeTop > 0 && (
                                <div className='absolute z-30 pointer-events-none' style={{ top: `${currentTimeTop}px`, left: '60px' }}>
                                    <div className='w-3 h-3 rounded-full bg-blue-600 border-2 border-white shadow -translate-x-1/2 -translate-y-1/2'></div>
                                </div>
                            )}
                        </div>

                        {/* Staff Columns */}
                        {displayUsers.map((user, userColIndex) => {
                            const userAppointments = getAppointmentsForUser(user._id)
                            const blockedRanges = getBlockedRanges(user._id)

                            return (
                                <div key={user._id} className='relative border-r border-gray-200 flex-1' style={{ minWidth: '220px' }}>

                                    {/* Hour grid lines & drag slots */}
                                    {hourLabels.map((h) => (
                                        <div key={h.hour} style={{ height: `${HOUR_HEIGHT}px` }} className='border-b border-gray-100 relative'>
                                            {/* 4 x 15-min sub-slots per hour for drag detection */}
                                            {[0, 15, 30, 45].map((min) => {
                                                const totalMin = h.hour * 60 + min
                                                const blocked = isSlotBlocked(totalMin, blockedRanges)
                                                const past = isSlotPast(totalMin)
                                                const disabled = blocked || past
                                                const inDrag = isSlotInDragRange(totalMin, user._id)

                                                return (
                                                    <div
                                                        key={min}
                                                        className={`absolute left-0 right-0 select-none ${disabled ? '' : 'cursor-pointer'}`}
                                                        style={{
                                                            top: `${(min / 60) * HOUR_HEIGHT}px`,
                                                            height: `${SLOT_HEIGHT}px`,
                                                            backgroundColor: inDrag ? '#c6dafc' : past ? '#f9fafb' : 'transparent'
                                                        }}
                                                        onMouseDown={(e) => {
                                                            if (!disabled) {
                                                                e.preventDefault()
                                                                handleDragStart({ totalMinutes: totalMin }, user._id)
                                                            }
                                                        }}
                                                        onMouseEnter={() => {
                                                            if (!disabled) handleDragMove({ totalMinutes: totalMin }, user._id)
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    ))}

                                    {/* Drag selection preview block */}
                                    {(isDragging || showSlotPopup) && dragUserId === user._id && dragStartSlot !== null && dragEndSlot !== null && (() => {
                                        // When popup is open, use editable times; otherwise use drag range
                                        const previewStart = showSlotPopup && popupStartMin !== null ? popupStartMin : getDragRange().start
                                        const previewEnd = showSlotPopup && popupEndMin !== null ? popupEndMin : getDragRange().end
                                        if (previewStart === null) return null
                                        const top = ((previewStart - 480) / 60) * HOUR_HEIGHT
                                        const height = ((previewEnd - previewStart) / 60) * HOUR_HEIGHT

                                        return (
                                            <div
                                                className='absolute left-2 right-2 rounded-lg z-[8] pointer-events-none'
                                                style={{
                                                    top: `${Math.max(0, top)}px`,
                                                    height: `${Math.max(SLOT_HEIGHT, height)}px`,
                                                    backgroundColor: 'rgba(66, 133, 244, 0.85)',
                                                }}
                                            >
                                                <div className='px-3 pt-2'>
                                                    <div className='text-white text-[12px] font-medium'>
                                                        {minutesToLabel(previewStart)} - {minutesToLabel(previewEnd)}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })()}

                                    {/* Appointment Cards */}
                                    {userAppointments.map((apt, aptIndex) => {
                                        const parsed = parseBillTime(apt.billTime)
                                        if (!parsed) return null

                                        // Calculate duration from slotStart/slotEnd or fallback
                                        let durationMinutes = SLOT_GAP_MINUTES
                                        if (apt.slotStart && apt.slotEnd) {
                                            const startParsed = parseBillTime(apt.slotStart)
                                            const endParsed = parseBillTime(apt.slotEnd)
                                            if (startParsed && endParsed && endParsed.totalMinutes > startParsed.totalMinutes) {
                                                durationMinutes = endParsed.totalMinutes - startParsed.totalMinutes
                                            }
                                        }

                                        const top = ((parsed.totalMinutes - 480) / 60) * HOUR_HEIGHT
                                        const height = (durationMinutes / 60) * HOUR_HEIGHT
                                        const color = getCardColor(userColIndex)

                                        const clientName = typeof apt.client === 'object' ? apt.client?.name : 'Walk-in'
                                        const serviceName = apt.services?.map(s =>
                                            typeof s.service === 'object' ? s.service?.name : (s.name || 'Service')
                                        ).join(', ') || 'Service'

                                        const timeDisplay = apt.slotStart && apt.slotEnd
                                            ? `${apt.slotStart} - ${apt.slotEnd}`
                                            : apt.billTime

                                        return (
                                            <div
                                                key={apt._id}
                                                className='absolute left-2 right-2 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow z-[5]'
                                                style={{
                                                    top: `${Math.max(0, top)}px`,
                                                    height: `${Math.max(HOUR_HEIGHT * 0.8, height)}px`,
                                                    backgroundColor: color.bg,
                                                    border: `1px solid ${color.border}`,
                                                    borderLeft: `3px solid ${color.border}`,
                                                }}
                                                title={`${clientName} - ${serviceName}`}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleViewAppointment(apt)
                                                }}
                                            >
                                                <div className='px-3 pt-2'>
                                                    <div className='text-[13px] font-bold text-gray-800 truncate leading-tight'>
                                                        {serviceName}
                                                    </div>
                                                    <div className='text-[11px] text-gray-500 mt-0.5'>
                                                        {timeDisplay}
                                                    </div>
                                                    {durationMinutes >= 60 && (
                                                        <div className='text-[11px] text-gray-400 mt-1 truncate'>
                                                            {clientName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Current time line */}
                                    {isToday && currentTimeTop > 0 && (
                                        <div className='absolute left-0 right-0 z-20 pointer-events-none' style={{ top: `${currentTimeTop}px` }}>
                                            <div className='h-[2px] bg-blue-600 w-full'></div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/* Loading overlay */}
            {isLoadingAppointments && (
                <div className='absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50'>
                    <div className='flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-lg border border-gray-100'>
                        <div className='animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full'></div>
                        <span className='text-sm text-gray-500'>Loading appointments...</span>
                    </div>
                </div>
            )}

            {/* Slot Selection Popup */}
            {showSlotPopup && popupStartMin !== null && popupEndMin !== null && (() => {
                const dateStr = selectedDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
                const duration = popupEndMin - popupStartMin
                const popX = Math.min(popupPosition.x + 16, window.innerWidth - 380)
                const popY = Math.min(popupPosition.y - 80, window.innerHeight - 360)
                return (
                    <div
                        ref={popupRef}
                        className='fixed z-[200] bg-white rounded-2xl shadow-2xl border border-gray-200 w-[360px] overflow-visible'
                        style={{ left: `${Math.max(10, popX)}px`, top: `${Math.max(10, popY)}px` }}
                    >
                        <div className='h-1.5 bg-blue-500 rounded-t-2xl'></div>
                        <div className='p-5'>
                            {/* Date & time row */}
                            <div className='flex items-start gap-3 mb-4'>
                                <div className='w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5'>
                                    <Clock className='w-4 h-4 text-blue-600' />
                                </div>
                                <div className='flex-1'>
                                    <p className='text-[15px] font-medium text-gray-900 mb-2'>{dateStr}</p>

                                    {/* Editable time pickers */}
                                    <div className='flex items-center gap-2'>
                                        {/* Start time */}
                                        <div className='relative' ref={startDropdownRef}>
                                            <button
                                                onClick={() => { setShowStartDropdown(!showStartDropdown); setShowEndDropdown(false) }}
                                                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${showStartDropdown ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {minutesToLabel(popupStartMin)}
                                            </button>
                                            {showStartDropdown && (
                                                <div className='absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[210] w-[160px] max-h-[200px] overflow-y-auto'>
                                                    {timeOptions.filter(t => t < popupEndMin && !isSlotPast(t) && !isSlotBlocked(t, getBlockedRanges(dragUserId))).map(t => (
                                                        <button
                                                            key={t}
                                                            onClick={() => { setPopupStartMin(t); setShowStartDropdown(false) }}
                                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${t === popupStartMin ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                        >
                                                            {minutesToLabel(t)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <span className='text-gray-400 text-sm'>&ndash;</span>

                                        {/* End time */}
                                        <div className='relative' ref={endDropdownRef}>
                                            <button
                                                onClick={() => { setShowEndDropdown(!showEndDropdown); setShowStartDropdown(false) }}
                                                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${showEndDropdown ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {minutesToLabel(popupEndMin)}
                                            </button>
                                            {showEndDropdown && (
                                                <div className='absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[210] w-[200px] max-h-[200px] overflow-y-auto'>
                                                    {timeOptions.filter(t => t > popupStartMin && t <= getNextBlockedStart(popupStartMin, dragUserId)).map(t => {
                                                        const dur = t - popupStartMin
                                                        return (
                                                            <button
                                                                key={t}
                                                                onClick={() => { setPopupEndMin(t); setShowEndDropdown(false) }}
                                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex justify-between ${t === popupEndMin ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                            >
                                                                <span>{minutesToLabel(t)}</span>
                                                                <span className='text-gray-400 text-xs'>({formatDuration(dur)})</span>
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Duration badge */}
                                        {duration > 0 && (
                                            <span className='text-xs text-gray-400 ml-1'>({formatDuration(duration)})</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className='flex items-center gap-2 pt-1'>
                                <button
                                    onClick={handleSlotPopupConfirm}
                                    className='flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors'
                                >
                                    Book Appointment
                                </button>
                                <button
                                    onClick={() => {
                                        setShowSlotPopup(false)
                                        setDragStartSlot(null)
                                        setDragEndSlot(null)
                                        setDragUserId(null)
                                        setShowStartDropdown(false)
                                        setShowEndDropdown(false)
                                    }}
                                    className='px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors'
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}

            {/* Appointment Details Modal */}
            {isModalOpen && selectedAppointment && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-[100]' onClick={handleCloseModal}>
                    <div className='bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[85vh] overflow-hidden' onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className='flex justify-between items-start px-6 py-5 border-b border-gray-100'>
                            <div>
                                <h2 className='text-lg font-bold text-gray-900'>
                                    {selectedAppointment.billNumber || 'Appointment'}
                                </h2>
                                <p className='text-sm text-gray-500 mt-0.5'>
                                    {selectedAppointment.billDate} {selectedAppointment.slotStart && selectedAppointment.slotEnd
                                        ? `${selectedAppointment.slotStart} - ${selectedAppointment.slotEnd}`
                                        : selectedAppointment.billTime}
                                </p>
                            </div>
                            <button onClick={handleCloseModal} className='p-2 hover:bg-gray-100 rounded-full transition-colors'>
                                <X className='h-5 w-5 text-gray-400' />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 p-6 max-h-[70vh] overflow-y-auto'>
                            {/* Left Column */}
                            <div className='lg:col-span-2 space-y-4'>
                                {/* Appointment Information */}
                                <div className='bg-blue-50/60 rounded-xl border border-blue-100 p-4'>
                                    <h3 className='text-sm font-semibold text-gray-800 mb-3'>Appointment Information</h3>
                                    <div className='grid grid-cols-3 gap-3 text-xs'>
                                        <div>
                                            <p className='text-gray-500'>Bill Number</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{selectedAppointment.billNumber || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Date</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{selectedAppointment.billDate || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Slot</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>
                                                {selectedAppointment.slotStart && selectedAppointment.slotEnd
                                                    ? `${selectedAppointment.slotStart} - ${selectedAppointment.slotEnd}`
                                                    : selectedAppointment.billTime || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Status</p>
                                            <div className='mt-1'>{renderBillStatus(selectedAppointment.status)}</div>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Transaction ID</p>
                                            <p className='font-medium text-gray-800 mt-0.5 truncate' title={selectedAppointment.transactionId}>{selectedAppointment.transactionId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Created</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{formatDate(selectedAppointment.timestamp)} {formatTime(selectedAppointment.timestamp)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Client Information */}
                                <div className='bg-gray-50 rounded-xl border border-gray-100 p-4'>
                                    <h3 className='text-sm font-semibold text-gray-800 mb-3'>Client</h3>
                                    <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs'>
                                        <div>
                                            <p className='text-gray-500'>Name</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{selectedAppointment.client?.name || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Phone</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{selectedAppointment.client?.phoneNumber || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Gender</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{selectedAppointment.client?.gender || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className='text-gray-500'>Wallet</p>
                                            <p className='font-medium text-gray-800 mt-0.5'>{formatCurrency(selectedAppointment.client?.walletId?.balance || 0)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Services */}
                                {Array.isArray(selectedAppointment.services) && selectedAppointment.services.length > 0 && (
                                    <div className='bg-gray-50 rounded-xl border border-gray-100 p-4'>
                                        <h3 className='text-sm font-semibold text-gray-800 mb-3'>
                                            Services ({selectedAppointment.services.length})
                                        </h3>
                                        <div className='space-y-2 max-h-48 overflow-y-auto'>
                                            {selectedAppointment.services.map((service, index) => (
                                                <div key={index} className='bg-white border border-gray-100 rounded-lg p-3 text-xs'>
                                                    <div className='flex justify-between items-start'>
                                                        <div className='flex-1'>
                                                            <p className='font-medium text-gray-900'>{service.name}</p>
                                                            <p className='text-gray-500 mt-0.5'>Qty: {service.quantity || 1}</p>
                                                            {service.staff && (() => {
                                                                const staffId = typeof service.staff === 'object' ? service.staff._id : service.staff
                                                                const staff = staffDetailsMap[staffId]
                                                                if (staff) {
                                                                    return (
                                                                        <div className='mt-1 flex items-center gap-1'>
                                                                            <User className='w-3 h-3 text-blue-500' />
                                                                            <span className='text-[10px] font-medium text-blue-500'>
                                                                                {staff.name}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }
                                                                return null
                                                            })()}
                                                        </div>
                                                        <div className='text-right'>
                                                            <p className='font-semibold text-gray-900'>
                                                                {formatCurrency(service.pricing?.finalPrice || service.pricing?.totalPrice || 0)}
                                                            </p>
                                                            {service.pricing?.basePrice !== service.pricing?.finalPrice && (
                                                                <p className='text-gray-400 line-through text-[10px]'>
                                                                    {formatCurrency(service.pricing?.basePrice || 0)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column */}
                            <div className='space-y-4'>
                                {/* Payment */}
                                <div className='bg-gray-50 rounded-xl border border-gray-100 p-4'>
                                    <h3 className='text-sm font-semibold text-gray-800 mb-3'>Payment</h3>
                                    <div className='space-y-2 text-xs'>
                                        <div>
                                            <p className='text-gray-500 mb-1'>Status</p>
                                            {renderPaymentStatus(selectedAppointment.payment)}
                                        </div>
                                        {selectedAppointment.payment?.activePaymentMethods?.length > 0 && (
                                            <div>
                                                <p className='text-gray-500 mb-1'>Methods</p>
                                                <div className='space-y-1'>
                                                    {selectedAppointment.payment.activePaymentMethods.map((method, index) => (
                                                        <div key={index} className='flex justify-between items-center bg-white p-2 rounded-lg'>
                                                            <span className='font-medium text-gray-700'>{method.method}</span>
                                                            <span className='text-gray-900'>{formatCurrency(method.amount)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className='pt-2 border-t border-gray-200'>
                                            <div className='flex justify-between items-center'>
                                                <span className='font-medium text-gray-700'>Total Paid</span>
                                                <span className='font-bold text-sm text-gray-900'>
                                                    {formatCurrency(selectedAppointment.payment?.totalPaid || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bill Summary */}
                                <div className='bg-gray-50 rounded-xl border border-gray-100 p-4'>
                                    <h3 className='text-sm font-semibold text-gray-800 mb-3'>Summary</h3>
                                    <div className='space-y-2 text-xs'>
                                        <div className='flex justify-between'>
                                            <span className='text-gray-500'>Subtotal</span>
                                            <span className='text-gray-900'>{formatCurrency(selectedAppointment.calculations?.totals?.subtotalBeforeDiscount || 0)}</span>
                                        </div>
                                        {selectedAppointment.calculations?.totals?.totalDiscount > 0 && (
                                            <div className='flex justify-between text-green-600'>
                                                <span>Discount</span>
                                                <span>-{formatCurrency(selectedAppointment.calculations?.totals?.totalDiscount || 0)}</span>
                                            </div>
                                        )}
                                        <div className='pt-2 border-t-2 border-gray-200'>
                                            <div className='flex justify-between'>
                                                <span className='text-sm font-semibold text-gray-900'>Total</span>
                                                <span className='text-base font-bold text-gray-900'>
                                                    {formatCurrency(selectedAppointment.calculations?.totals?.finalAmount || selectedAppointment.calculations?.totals?.grandTotal || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <button
                                    onClick={() => {
                                        handleCloseModal()
                                        navigate(`/appoinments/edit/${selectedAppointment._id}`)
                                    }}
                                    className='w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm'
                                >
                                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                    </svg>
                                    Edit Appointment
                                </button>

                                <button
                                    onClick={handleDeleteAppointment}
                                    className='w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm border border-red-200'
                                >
                                    <Trash2 className='w-4 h-4' />
                                    Delete
                                </button>

                                {selectedAppointment.remarks && (
                                    <div className='bg-gray-50 rounded-xl border border-gray-100 p-4'>
                                        <h3 className='text-sm font-semibold text-gray-800 mb-1'>Remarks</h3>
                                        <p className='text-xs text-gray-600'>{selectedAppointment.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AppoinmentMaster
